import { IFormControlsResolver } from 'src/features/forms/base/controls/IFormControlsResolver';
import { ReactComponent as ContainerIcon } from 'assets/img/formBuilder/container_24.svg';
import { ReactComponent as TextIcon } from 'assets/img/formBuilder/text_24.svg';
import { ReactComponent as TextInputIcon } from 'assets/img/formBuilder/multiple_line_24.svg';
import { ReactComponent as NumberInputIcon } from 'assets/img/formBuilder/number_24.svg';
import { ReactComponent as SliderInputIcon } from 'assets/img/formBuilder/slider_24.svg';
import { ReactComponent as DateInputIcon } from 'assets/img/formBuilder/calendar_24.svg';
import { ReactComponent as DateRangeInputIcon } from 'assets/img/formBuilder/calendar_range_24.svg';
import { ReactComponent as TimeInputIcon } from 'assets/img/formBuilder/clock_24.svg';
import { ReactComponent as CheckBoxInputIcon } from 'assets/img/formBuilder/checkbox_24.svg';
import { ReactComponent as RadioInputIcon } from 'assets/img/formBuilder/radio_24.svg';
import { ReactComponent as DropdownInputIcon } from 'assets/img/formBuilder/dropdown_24.svg';
import { ReactComponent as FileInputIcon } from 'assets/img/formBuilder/file_24.svg';
import { ReactComponent as ImageInputIcon } from 'assets/img/formBuilder/pic_24.svg';
import { ReactComponent as TagInputIcon } from 'assets/img/formBuilder/tag_box_24.svg';
import { ReactComponent as FormulaIcon } from 'assets/img/formBuilder/formula_24.svg';
import { ReactComponent as EmptyBlockIcon } from 'assets/img/formBuilder/empty_block_24.svg';
import { ReactComponent as AttentionTextIcon } from 'assets/img/formBuilder/attention_text_24.svg';
import { ReactComponent as StepIcon } from 'assets/img/formBuilder/step_24.svg';
import { ReactComponent as TabsIcon } from 'assets/img/formBuilder/tabs_24.svg';
import { Element } from '@craftjs/core';
import { FieldGroupContainer, FieldGroupContainerProps } from './Containers/FieldGroupContainer';
import { Container } from './Containers/Container';
import { StepContainer } from './Containers/StepContainer';
import { TabsContainer } from './Containers/TabsContainer';
import { AttentionText } from './content/AttentionText';
import { Text } from './content/Text';
import { NumberInput } from './Inputs/NumberInput';
import { SliderInput } from './Inputs/SliderInput';
import { DateInput } from './Inputs/DateInput';
import { TimeInput } from './Inputs/TimeInput';
import { CheckBoxInput } from './Inputs/CheckBoxInput';
import { CheckBoxGroupInput } from './Inputs/CheckBoxGroupInput';
import { RadioInput } from './Inputs/RadioInput';
import { SelectInput } from './Inputs/SelectInput';
import { Files } from './Inputs/Files';
import { DateRangeInput } from './Inputs/DateRangeInput';
import { TagInput } from './Inputs/TagInput';
import { BmiInputV2 } from './ConstantBlocks/BmiInputV2';
import { DateIntervalCalc } from './ConstantBlocks/DateIntervalCalc';
import { RecordResultList } from './ConstantBlocks/RecordResultList/RecordResultList';
import { CalculatedBlock } from './Containers/CalculatedBlock';
import { BmiInput } from './ConstantBlocks/BmiInput';
import { EmptyBlock } from './content/EmptyBlock';
import { ImageBlock } from './content/ImageBlock';
import { TextInput } from './Inputs/TextInput';
import { textInputDefaultPropsFactory } from 'src/features/forms/base/controls/inputs/TextInput';
import { CONST_DATA_KEYS } from '../uiEditor/uiEditor-helper';
import { bmiInputV2DefaultPropsFactory } from 'src/features/forms/base/controls/specials/BmiInputV2';
import { UIEditorBlockWidth } from 'src/features/forms/base/controls/inputs/base/baseControlSettings';
import { useStudy } from 'src/helpers/hooks/useStudy';
import { dateIntervalCalcDefaultPropsFactory } from 'src/features/forms/base/controls/specials/DateIntervalCalc';
import { numberInputDefaultPropsFactory } from 'src/features/forms/base/controls/inputs/INumberInput';
import { sliderInputDefaultPropsFactory } from 'src/features/forms/base/controls/inputs/SliderInput';
import { dateInputDefaultPropsFactory } from 'src/features/forms/base/controls/inputs/DateInput';
import { dateRangeInputDefaultPropsFactory } from 'src/features/forms/base/controls/inputs/DateRangeInput';
import { timeInputDefaultPropsFactory } from 'src/features/forms/base/controls/inputs/TimeInput';
import { radioInputDefaultPropsFactory } from 'src/features/forms/base/controls/inputs/RadioInput';
import { selectInputDefaultPropsFactory } from 'src/features/forms/base/controls/inputs/SelectInput';
import { checkBoxInputDefaultPropsFactory } from 'src/features/forms/base/controls/inputs/CheckBoxInput';
import { checkBoxGroupInputDefaultPropsFactory } from 'src/features/forms/base/controls/inputs/CheckBoxGroupInput';
import { tagInputDefaultPropsFactory } from 'src/features/forms/base/controls/inputs/TagInputProps';
import { filesDefaultPropsFactory } from 'src/features/forms/base/controls/inputs/Files';
import { PatientGroupInput } from './ConstantBlocks/PatientGroupInput';
import { useTranslation } from 'react-i18next';
import { SelectorGroupEnum, SelectorResolverType } from 'src/features/generalForms/builder/generalFormBuilder.t';

type FormSelector = SelectorResolverType<IFormControlsResolver>;

export const useSelectors = () => {
  const { hasEcrf } = useStudy()!;
  const { t } = useTranslation('dev');

  return {
    // Containers
    Container: {
      Icon: ContainerIcon,
      name: t('StudySettingsPage.FormBuilder.blocks.container'),
      group: SelectorGroupEnum.Containers,
      userComponent: <Element canvas={true} is={Container} isPadding={true} flexBoxGap={12} />,
    },
    FieldGroupContainer: {
      Icon: ContainerIcon,
      name: t('StudySettingsPage.FormBuilder.blocks.fieldGroupContainer'),
      group: SelectorGroupEnum.Containers,
      userComponent: <Element is={FieldGroupContainer} {...FieldGroupContainerProps} useForNavigation={true} />,
    },
    StepContainer: {
      Icon: StepIcon,
      name: t('StudySettingsPage.FormBuilder.blocks.stepContainer'),
      group: SelectorGroupEnum.Containers,
      userComponent: <Element is={StepContainer} />,
    },
    TabsContainer: {
      Icon: TabsIcon,
      name: t('StudySettingsPage.FormBuilder.blocks.tabsContainer'),
      group: SelectorGroupEnum.Containers,
      userComponent: <Element is={TabsContainer} />,
    },
    // Content
    Text: {
      Icon: TextIcon,
      name: t('StudySettingsPage.FormBuilder.blocks.contentBlocks.text.label'),
      group: SelectorGroupEnum.Content,
      userComponent: <Element is={Text} size={UIEditorBlockWidth.Stretch} />,
    },
    AttentionText: {
      Icon: AttentionTextIcon,
      name: t('StudySettingsPage.FormBuilder.blocks.contentBlocks.attentionText.label'),
      group: SelectorGroupEnum.Content,
      userComponent: <Element is={AttentionText} size={UIEditorBlockWidth.Stretch} />,
    },
    ImageBlock: {
      Icon: ImageInputIcon,
      name: t('StudySettingsPage.FormBuilder.blocks.contentBlocks.image.label'),
      group: SelectorGroupEnum.Content,
      userComponent: (
        <Element
          is={ImageBlock}
          fileId={null}
          isFill={true}
          isHiddenOnOverview={true}
          size={UIEditorBlockWidth.Stretch}
        />
      ),
    },
    // Const fields
    PatientId: {
      Icon: TextInputIcon,
      name: t('StudySettingsPage.FormBuilder.blocks.constFields.patientId.label'),
      group: SelectorGroupEnum.ConstFields,
      userComponent: (
        <Element
          is={TextInput}
          {...textInputDefaultPropsFactory()}
          hasSkipCheckBox={false}
          dataKey={CONST_DATA_KEYS.UniqueId}
          label={t('StudySettingsPage.FormBuilder.blocks.constFields.patientId.label')}
          placeholder={t('StudySettingsPage.FormBuilder.blocks.constFields.patientId.placeholder')}
          isNumberOnly={true}
        />
      ),
    },
    DeviceSn: {
      Icon: NumberInputIcon,
      name: t('StudySettingsPage.FormBuilder.blocks.constFields.deviceSn.label'),
      group: SelectorGroupEnum.ConstFields,
      userComponent: (
        <Element
          is={TextInput}
          {...textInputDefaultPropsFactory()}
          dataKey={CONST_DATA_KEYS.DeviceSerialNumber}
          hasSkipCheckBox={hasEcrf}
          label={t('StudySettingsPage.FormBuilder.blocks.constFields.deviceSn.label')}
          placeholder={t('StudySettingsPage.FormBuilder.blocks.constFields.deviceSn.placeholder')}
        />
      ),
    },
    ContractSignedAt: {
      Icon: DateInputIcon,
      name: t('StudySettingsPage.FormBuilder.blocks.constFields.contractSigningDate.label'),
      group: SelectorGroupEnum.ConstFields,
      userComponent: (
        <Element
          is={DateInput}
          {...dateInputDefaultPropsFactory()}
          dataKey={CONST_DATA_KEYS.ContractSignedAt}
          hasSkipCheckBox={hasEcrf}
          label={t('StudySettingsPage.FormBuilder.blocks.constFields.contractSigningDate.label')}
        />
      ),
    },
    StudyStartedAt: {
      Icon: DateInputIcon,
      name: t('StudySettingsPage.FormBuilder.blocks.constFields.studyStartDate.label'),
      group: SelectorGroupEnum.ConstFields,
      userComponent: (
        <Element
          is={DateInput}
          {...dateInputDefaultPropsFactory()}
          dataKey={CONST_DATA_KEYS.StudyStartedAt}
          hasSkipCheckBox={hasEcrf}
          label={t('StudySettingsPage.FormBuilder.blocks.constFields.studyStartDate.label')}
        />
      ),
    },
    GroupInput: {
      Icon: ContainerIcon,
      name: t('StudySettingsPage.FormBuilder.blocks.constFields.group.label'),
      group: SelectorGroupEnum.ConstFields,
      userComponent: (
        <Element
          is={PatientGroupInput}
          {...radioInputDefaultPropsFactory()}
          dataKey={CONST_DATA_KEYS.GroupId}
          label={t('StudySettingsPage.FormBuilder.blocks.constFields.group.label')}
        />
      ),
    },
    BmiInputV2: {
      Icon: FormulaIcon,
      name: t('StudySettingsPage.FormBuilder.blocks.constFields.bmi.label'),
      group: SelectorGroupEnum.ConstFields,
      userComponent: (
        <Element
          is={BmiInputV2}
          {...bmiInputV2DefaultPropsFactory()}
          size={UIEditorBlockWidth.Stretch}
          hasSkipCheckBox={hasEcrf}
          flexBoxGap={20}
          isFlexBox={true}
        />
      ),
    },
    DateIntervalCalc: {
      Icon: FormulaIcon,
      name: t('StudySettingsPage.FormBuilder.blocks.constFields.dateInterval.label'),
      group: SelectorGroupEnum.ConstFields,
      userComponent: (
        <Element
          is={DateIntervalCalc}
          {...dateIntervalCalcDefaultPropsFactory()}
          size={UIEditorBlockWidth.Stretch}
          hasSkipCheckBox={hasEcrf}
          flexBoxGap={20}
          isFlexBox={true}
        />
      ),
    },
    RecordResultList: {
      Icon: TagInputIcon,
      name: t('StudySettingsPage.FormBuilder.blocks.constFields.recordResultList.label'),
      group: SelectorGroupEnum.ConstFields,
      userComponent: <Element is={RecordResultList} dataKey={CONST_DATA_KEYS.RecordResultList} />,
    },

    // Fields
    TextInput: {
      Icon: TextInputIcon,
      name: t('StudySettingsPage.FormBuilder.blocks.fields.text.label'),
      group: SelectorGroupEnum.InputFields,
      userComponent: (
        <Element
          is={TextInput}
          {...textInputDefaultPropsFactory()}
          hasSkipCheckBox={hasEcrf}
          label={t('StudySettingsPage.FormBuilder.blocks.fields.text.label')}
        />
      ),
    },
    NumberInput: {
      Icon: NumberInputIcon,
      name: t('StudySettingsPage.FormBuilder.blocks.fields.number.label'),
      group: SelectorGroupEnum.InputFields,
      userComponent: (
        <Element
          is={NumberInput}
          {...numberInputDefaultPropsFactory()}
          hasSkipCheckBox={hasEcrf}
          label={t('StudySettingsPage.FormBuilder.blocks.fields.number.label')}
        />
      ),
    },
    SliderInput: {
      Icon: SliderInputIcon,
      name: t('StudySettingsPage.FormBuilder.blocks.fields.slider.label'),
      group: SelectorGroupEnum.InputFields,
      userComponent: (
        <Element
          is={SliderInput}
          {...sliderInputDefaultPropsFactory()}
          hasSkipCheckBox={hasEcrf}
          label={t('StudySettingsPage.FormBuilder.blocks.fields.slider.label')}
        />
      ),
    },
    DateInput: {
      Icon: DateInputIcon,
      name: t('StudySettingsPage.FormBuilder.blocks.fields.date.label'),
      group: SelectorGroupEnum.InputFields,
      userComponent: (
        <Element
          is={DateInput}
          {...dateInputDefaultPropsFactory()}
          hasSkipCheckBox={hasEcrf}
          label={t('StudySettingsPage.FormBuilder.blocks.fields.date.label')}
        />
      ),
    },
    DateRangeInput: {
      Icon: DateRangeInputIcon,
      name: t('StudySettingsPage.FormBuilder.blocks.fields.dateRange.label'),
      group: SelectorGroupEnum.InputFields,
      userComponent: (
        <Element
          is={DateRangeInput}
          {...dateRangeInputDefaultPropsFactory()}
          hasSkipCheckBox={hasEcrf}
          label={t('StudySettingsPage.FormBuilder.blocks.fields.dateRange.label')}
        />
      ),
    },
    TimeInput: {
      Icon: TimeInputIcon,
      name: t('StudySettingsPage.FormBuilder.blocks.fields.time.label'),
      group: SelectorGroupEnum.InputFields,
      userComponent: (
        <Element
          is={TimeInput}
          {...timeInputDefaultPropsFactory()}
          hasSkipCheckBox={hasEcrf}
          label={t('StudySettingsPage.FormBuilder.blocks.fields.time.label')}
        />
      ),
    },
    RadioInput: {
      Icon: RadioInputIcon,
      name: t('StudySettingsPage.FormBuilder.blocks.fields.radioGroup.label'),
      group: SelectorGroupEnum.InputFields,
      userComponent: (
        <Element
          is={RadioInput}
          {...radioInputDefaultPropsFactory()}
          hasSkipCheckBox={hasEcrf}
          label={t('StudySettingsPage.FormBuilder.blocks.fields.radioGroup.label')}
        />
      ),
    },
    SelectInput: {
      Icon: DropdownInputIcon,
      name: t('StudySettingsPage.FormBuilder.blocks.fields.dropDown.label'),
      group: SelectorGroupEnum.InputFields,
      userComponent: (
        <Element
          is={SelectInput}
          {...selectInputDefaultPropsFactory()}
          hasSkipCheckBox={hasEcrf}
          label={t('StudySettingsPage.FormBuilder.blocks.fields.dropDown.label')}
        />
      ),
    },
    CheckBoxInput: {
      Icon: CheckBoxInputIcon,
      name: t('StudySettingsPage.FormBuilder.blocks.fields.checkBox.label'),
      group: SelectorGroupEnum.InputFields,
      userComponent: (
        <Element
          is={CheckBoxInput}
          {...checkBoxInputDefaultPropsFactory()}
          hasSkipCheckBox={hasEcrf}
          label={t('StudySettingsPage.FormBuilder.blocks.fields.checkBox.label')}
        />
      ),
    },
    CheckBoxGroupInput: {
      Icon: CheckBoxInputIcon,
      name: t('StudySettingsPage.FormBuilder.blocks.fields.checkBoxGroup.label'),
      group: SelectorGroupEnum.InputFields,
      userComponent: (
        <Element
          is={CheckBoxGroupInput}
          {...checkBoxGroupInputDefaultPropsFactory()}
          hasSkipCheckBox={hasEcrf}
          label={t('StudySettingsPage.FormBuilder.blocks.fields.checkBoxGroup.label')}
        />
      ),
    },
    TagInput: {
      Icon: TagInputIcon,
      name: t('StudySettingsPage.FormBuilder.blocks.fields.tagInput.label'),
      group: SelectorGroupEnum.InputFields,
      userComponent: (
        <Element
          is={TagInput}
          {...tagInputDefaultPropsFactory()}
          hasSkipCheckBox={hasEcrf}
          label={t('StudySettingsPage.FormBuilder.blocks.fields.tagInput.label')}
        />
      ),
    },
    Files: {
      Icon: FileInputIcon,
      name: t('StudySettingsPage.FormBuilder.blocks.fields.fileField.label'),
      group: SelectorGroupEnum.InputFields,
      userComponent: (
        <Element
          is={Files}
          {...filesDefaultPropsFactory()}
          hasSkipCheckBox={hasEcrf}
          label={t('StudySettingsPage.FormBuilder.blocks.fields.fileField.label')}
        />
      ),
    },
    // Not displayed
    CalculatedBlock: {
      Icon: ContainerIcon,
      name: '',
      group: SelectorGroupEnum.None,
      userComponent: <CalculatedBlock dataKey={''} size={''} />,
    },
    BmiInput: {
      Icon: FormulaIcon,
      name: t('StudySettingsPage.FormBuilder.blocks.constFields.bmi.label'),
      group: SelectorGroupEnum.None,
      userComponent: <BmiInput dataKey={''} validation={{}} size={''} />,
    },
    EmptyBlock: {
      Icon: EmptyBlockIcon,
      name: t('StudySettingsPage.FormBuilder.blocks.empty'),
      group: SelectorGroupEnum.None,
      userComponent: <EmptyBlock />,
    },
  } satisfies FormSelector;
};
